import { Stack } from '@mui/material';
import { NumberField, Select, TagInput, TextField } from 'components/commons';
import i18next from 'i18next';
import { AddUserFormType } from 'pages/UserAdd';
import React, { FC, useEffect, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import ja from 'utils/ja';
import { getTagsLS } from 'utils/localTag';

enum Gender {
  MALE = 'male',
  FEMALE = 'female',
}

const SelectGenderOpts: Option[] = [
  { label: ja.common.male, value: Gender.MALE },
  { label: ja.common.female, value: Gender.FEMALE },
];

interface UserFormProps {
  control: Control<AddUserFormType>;
  isEdit?: boolean;
}

const UserForm: FC<UserFormProps> = ({ control, isEdit = false }) => {
  const [tagOpts, setTagOpts] = useState<Option[]>([]);

  const handleGetListTags = async () => {
    try {
      const data = getTagsLS();
      setTagOpts(
        data.map((tag: ITag) => ({ value: tag.id, label: tag.tagName }))
      );
    } catch (error) {
      /* empty */
    }
  };

  useEffect(() => {
    handleGetListTags();
  }, []);

  return (
    <Stack
      gap='10px'
      my='10px'
      sx={{
        '& *': { fontSize: '18px !important' },
      }}
    >
      <Controller
        name='displayName'
        control={control}
        rules={{
          required: true,
          maxLength: {
            value: 20,
            message: i18next.t(ja.message_error.max_length, {
              0: ja.user_add.display_name,
              1: '20',
            }),
          },
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            id='displayName'
            label={ja.user_add.display_name}
            placeholder={ja.user_add.display_name_placeholder}
            require
            maxLength={20}
            error={fieldState.error}
            disabled={isEdit}
          />
        )}
      />
      <Controller
        name='affiliation'
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            id='affiliation'
            label={ja.user_add.affiliation}
            placeholder={ja.user_add.affiliation_placeholder}
            disabled={isEdit}
          />
        )}
      />
      <Controller
        name='tag'
        control={control}
        render={({ field }) => (
          <TagInput
            id='tag'
            label={ja.user_add.tag}
            placeholder={ja.user_add.tag_placeholder}
            limitTags={3}
            value={field.value}
            onChange={(value: string) => field.onChange(value)}
            options={tagOpts}
            disabled={isEdit}
          />
        )}
      />
      <Controller
        name='gender'
        control={control}
        render={({ field }) => (
          <Select
            id='gender'
            label={ja.user_add.gender}
            placeholder={ja.user_add.gender_placeholder}
            options={SelectGenderOpts}
            notSetOpt
            value={field.value}
            onChange={(value) => field.onChange(value)}
            disabled={isEdit}
          />
        )}
      />
      <Controller
        name='age'
        control={control}
        rules={{
          validate: (value) => {
            if (Number(value) > 150) {
              return ja.message_error.max_age;
            }
          },
        }}
        render={({ field, fieldState }) => (
          <NumberField
            {...field}
            id='age'
            label={ja.user_add.age}
            placeholder={ja.user_add.age_placeholder}
            error={fieldState.error}
            disabled={isEdit}
          />
        )}
      />
      <Controller
        name='group'
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            id='group'
            label={ja.user_add.group}
            placeholder={ja.user_add.group_placeholder}
            disabled={isEdit}
          />
        )}
      />
      <Controller
        name='idNumber'
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            id='idNumber'
            label={ja.user_add.id_number}
            placeholder={ja.user_add.id_number_placeholder}
            disabled={isEdit}
          />
        )}
      />
      <Controller
        name='phoneNumber'
        control={control}
        rules={{
          maxLength: {
            value: 11,
            message: ja.message_error.range_phone,
          },
          minLength: {
            value: 10,
            message: ja.message_error.range_phone,
          },
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            id='phoneNumber'
            label={ja.user_add.phone_number}
            placeholder={ja.user_add.phone_number_placeholder}
            type='number'
            error={fieldState.error}
            maxLength={11}
            onKeyDown={(e) => {
              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            disabled={isEdit}
          />
        )}
      />
    </Stack>
  );
};

export default UserForm;
